// service/Matriculas.js
import instance from "../apis/app";
import Recintos from "./Recintos";

// ========================================
// 🆕 VALIDADORES LOCALES (Cliente)
// ========================================

/**
 * Validar formato de RUT chileno
 */
const validarRUT = (rut) => {
    if (!rut) return false;
    const rutRegex = /^[0-9]{1,2}\.?[0-9]{3}\.?[0-9]{3}-[0-9Kk]$/;
    return rutRegex.test(rut);
};

/**
 * Validar formato de email
 */
const validarEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validar ObjectId de MongoDB
 */
const validarObjectId = (id) => {
    if (!id) return false;
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    return objectIdRegex.test(id);
};

// ========================================
// CONFIGURACIÓN DE MATRÍCULAS (ADMIN)
// ========================================

/**
 * Configurar matrícula para un recinto (Admin)
 * @param {Object} data - Datos de configuración
 * @param {string} data.recintoId - ID del recinto
 * @param {number} data.valorMatricula - Valor de la matrícula
 * @param {string} data.tipoVigencia - 'indefinida' o 'anual'
 * @param {string} data.descripcion - Descripción opcional
 * @returns {Promise<object>} Respuesta con datos de la matrícula configurada
 */
const ConfigurarMatricula = async (data) => {
    try {
        // 🆕 Validaciones cliente
        if (!data.recintoId || !validarObjectId(data.recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        if (data.valorMatricula === undefined || data.valorMatricula < 0) {
            throw {
                success: false,
                error: 'Valor de matrícula inválido',
                code: 'INVALID_VALUE'
            };
        }

        if (data.tipoVigencia && !['indefinida', 'anual'].includes(data.tipoVigencia)) {
            throw {
                success: false,
                error: 'Tipo de vigencia debe ser "indefinida" o "anual"',
                code: 'INVALID_VIGENCIA'
            };
        }

        const response = await instance.post("/matriculas/configurar", data);
        return response.data; // 🆕 Consistencia: siempre .data
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Obtener configuración de matrícula de un recinto
 * @param {string} recintoId - ID del recinto
 * @returns {Promise<object>} Datos de la matrícula del recinto
 */
const ObtenerMatriculaRecinto = async (recintoId) => {
    try {
        // 🆕 Validación cliente
        if (!validarObjectId(recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const response = await instance.get(`/matriculas/recinto/${recintoId}`);
        return response.data;
    } catch (error) {
        // 🆕 Manejo de 404
        if (error.response?.status === 404) {
            return {
                success: false,
                error: 'No hay matrícula configurada para este recinto',
                code: 'MATRICULA_NOT_FOUND'
            };
        }
        throw error.response?.data || error;
    }
};

/**
 * Verificar si un recinto tiene configuración de matrícula
 * @param {string} recintoId - ID del recinto
 * @returns {Promise<object>} Información de configuración
 */
const VerificarConfiguracionRecinto = async (recintoId) => {
    try {
        // 🆕 Validación cliente
        if (!recintoId || recintoId === 'undefined' || recintoId === 'null') {
            return { 
                tieneConfiguracion: false,
                message: 'ID de recinto inválido'
            };
        }

        if (!validarObjectId(recintoId)) {
            return { 
                tieneConfiguracion: false,
                message: 'Formato de ID de recinto inválido'
            };
        }

        const response = await instance.get(`/matriculas/recinto/${recintoId}/configuracion`);
        return response; // 🆕 Consistencia
    } catch (error) {
        if (error.response?.status === 404) {
            return { 
                tieneConfiguracion: false,
                message: 'Este recinto no tiene configuración de matrícula'
            };
        }
        throw error.response?.data || error;
    }
};

/**
 * Listar todas las configuraciones de matrícula (Admin)
 * @param {Object} params - Parámetros de búsqueda
 * @param {boolean} params.activa - Filtrar por estado activo
 * @param {string} params.tipoVigencia - Filtrar por tipo de vigencia
 * @returns {Promise<object>} Lista de matrículas
 */
const ListarMatriculas = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        if (params.activa !== undefined) queryParams.append('activa', params.activa);
        if (params.tipoVigencia) queryParams.append('tipoVigencia', params.tipoVigencia);
        
        const queryString = queryParams.toString();
        const url = `/matriculas${queryString ? `?${queryString}` : ''}`;
        
        const response = await instance.get(url);
        return response; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Desactivar configuración de matrícula (Admin)
 * @param {string} recintoId - ID del recinto
 * @returns {Promise<object>} Respuesta con matrícula desactivada
 */
const DesactivarMatricula = async (recintoId) => {
    try {
        // 🆕 Validación cliente
        if (!validarObjectId(recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const response = await instance.delete(`/matriculas/recinto/${recintoId}/configuracion`);
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Recintos que HOY tienen matrícula a la venta.
 *
 * `/matriculas/recinto/:id/configuracion` solo responde con configuraciones
 * ACTIVAS, así que esta lista es la fuente de verdad para decidir si mostrar
 * o no las llamadas a comprar matrícula: si viene vacía, la matrícula no se
 * vende y no hay que ofrecerla.
 *
 * @returns {Promise<Array>} recintos con su `configuracionMatricula`
 */
const ObtenerRecintosConMatricula = async () => {
    const respuesta = await Recintos.List();
    const recintos = respuesta?.data || [];

    const disponibles = [];

    for (const recinto of recintos) {
        try {
            const config = await VerificarConfiguracionRecinto(recinto._id);

            if (config?.tieneConfiguracion) {
                disponibles.push({
                    ...recinto,
                    configuracionMatricula: config.configuracion
                });
            }
        } catch (error) {
            console.error(`Error verificando matrícula del recinto ${recinto._id}:`, error);
        }
    }

    return disponibles;
};

// ========================================
// VALIDACIÓN Y VERIFICACIÓN
// ========================================

/**
 * Verificar si un usuario tiene matrícula vigente
 * @param {string} rut - RUT del usuario
 * @param {string} recintoId - ID del recinto
 * @returns {Promise<object>} Información de verificación
 */
const VerificarMatriculaUsuario = async (rut, recintoId) => {
    try {
        console.log(rut)
        // 🆕 Validaciones cliente
        if (!validarRUT(rut)) {
            throw {
                success: false,
                error: 'Formato de RUT inválido',
                code: 'INVALID_RUT',
                rut: rut
            };
        }

        if (!validarObjectId(recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const response = await instance.get(`/matriculas/verificar/${rut}/${recintoId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Validar matrícula antes de inscripción (usado por carrito)
 * @param {Object} data - Datos de validación
 * @param {string} data.rut - RUT del usuario
 * @param {string} data.recintoId - ID del recinto
 * @returns {Promise<object>} Resultado de la validación
 */
const ValidarParaInscripcion = async (data) => {
    try {
        // 🆕 Validaciones cliente
        if (!data.rut || !validarRUT(data.rut)) {
            throw {
                success: false,
                error: 'RUT inválido',
                code: 'INVALID_RUT'
            };
        }

        if (!data.recintoId || !validarObjectId(data.recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const response = await instance.post("/matriculas/validar-inscripcion", data);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// ========================================
// PROCESO DE COMPRA (USUARIO)
// ========================================

/**
 * Crear pago de matrícula e iniciar WebPay
 * @param {Object} data - Datos del pago
 * @param {string} data.recintoId - ID del recinto
 * @param {Object} data.datosUsuario - Datos del usuario
 * @returns {Promise<object>} Respuesta con orden, pago y URL de WebPay
 */
const CrearPagoMatricula = async (data) => {
    try {
        // 🆕 Validaciones cliente
        if (!data.recintoId || !validarObjectId(data.recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        if (!data.datosUsuario) {
            throw {
                success: false,
                error: 'Datos de usuario requeridos',
                code: 'MISSING_USER_DATA'
            };
        }

        const { rut, nombre, email } = data.datosUsuario;

        if (!rut || !validarRUT(rut)) {
            throw {
                success: false,
                error: 'RUT inválido',
                code: 'INVALID_RUT'
            };
        }

        if (!nombre || nombre.trim().length < 3) {
            throw {
                success: false,
                error: 'Nombre inválido (mínimo 3 caracteres)',
                code: 'INVALID_NAME'
            };
        }

        if (!email || !validarEmail(email)) {
            throw {
                success: false,
                error: 'Email inválido',
                code: 'INVALID_EMAIL'
            };
        }

        const response = await instance.post("/matriculas/pago/crear", data);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Confirmar pago de matrícula (callback WebPay)
 * @param {Object} data - Datos de confirmación
 * @param {string} data.pagoId - ID del pago
 * @param {string} data.token_ws - Token de WebPay
 * @returns {Promise<object>} Respuesta con pago confirmado y matrícula
 */
const ConfirmarPagoMatricula = async (data) => {
    try {
        // 🆕 Validaciones cliente
        if (!data.pagoId || !validarObjectId(data.pagoId)) {
            throw {
                success: false,
                error: 'ID de pago inválido',
                code: 'INVALID_PAYMENT_ID'
            };
        }

        if (!data.token_ws) {
            throw {
                success: false,
                error: 'Token de WebPay requerido',
                code: 'MISSING_TOKEN'
            };
        }

        const response = await instance.post("/matriculas/pago/confirmar", data);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// ========================================
// GESTIÓN DE USUARIOS MATRICULADOS (ADMIN)
// ========================================

/**
 * Listar usuarios matriculados por recinto (Admin)
 * @param {string} recintoId - ID del recinto
 * @param {Object} params - Parámetros de filtrado
 * @returns {Promise<object>} Lista de usuarios matriculados
 */
const ListarUsuariosMatriculados = async (recintoId, params = {}) => {
    try {
        // 🆕 Validación cliente
        if (!validarObjectId(recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const queryParams = new URLSearchParams();
        
        if (params.vigente !== undefined) queryParams.append('vigente', params.vigente);
        if (params.tipoRegistro) queryParams.append('tipoRegistro', params.tipoRegistro);
        if (params.estadoPago) queryParams.append('estadoPago', params.estadoPago);
        
        const queryString = queryParams.toString();
        const url = `/matriculas/usuarios/${recintoId}${queryString ? `?${queryString}` : ''}`;
        
        const response = await instance.get(url);
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Agregar matrícula manual (Admin)
 * @param {Object} data - Datos de la matrícula manual
 * @returns {Promise<object>} Respuesta con matrícula creada
 */
const AgregarMatriculaManual = async (data) => {
    try {
        // 🆕 Validaciones cliente
        if (!data.rut || !validarRUT(data.rut)) {
            throw {
                success: false,
                error: 'RUT inválido',
                code: 'INVALID_RUT'
            };
        }

        if (!data.recintoId || !validarObjectId(data.recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        if (!data.datosUsuario || !data.datosUsuario.nombre || !data.datosUsuario.email) {
            throw {
                success: false,
                error: 'Datos de usuario incompletos',
                code: 'MISSING_USER_DATA'
            };
        }

        const response = await instance.post("/matriculas/manual", data);
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Obtener historial de matrículas de un usuario (Admin)
 * @param {string} rut - RUT del usuario
 * @returns {Promise<object>} Historial de matrículas
 */
const ObtenerHistorialUsuario = async (rut) => {
    try {
        // 🆕 Validación cliente
        if (!validarRUT(rut)) {
            throw {
                success: false,
                error: 'Formato de RUT inválido',
                code: 'INVALID_RUT'
            };
        }

        const response = await instance.get(`/matriculas/historial/${rut}`);
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Desactivar matrícula de un usuario (Admin)
 * @param {string} matriculaId - ID de la matrícula
 * @param {string} motivo - Motivo de desactivación
 * @returns {Promise<object>} Respuesta con matrícula desactivada
 */
const DesactivarMatriculaUsuario = async (matriculaId, motivo) => {
    try {
        // 🆕 Validaciones cliente
        if (!validarObjectId(matriculaId)) {
            throw {
                success: false,
                error: 'ID de matrícula inválido',
                code: 'INVALID_MATRICULA_ID'
            };
        }

        if (!motivo || motivo.trim().length < 5) {
            throw {
                success: false,
                error: 'Motivo requerido (mínimo 5 caracteres)',
                code: 'INVALID_REASON'
            };
        }

        const response = await instance.put(`/matriculas/desactivar/${matriculaId}`, {
            motivo
        });
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Obtener estadísticas de matrículas por recinto (Admin)
 * @param {string} recintoId - ID del recinto
 * @returns {Promise<object>} Estadísticas de matrículas
 */
const ObtenerEstadisticas = async (recintoId) => {
    try {
        // 🆕 Validación cliente
        if (!validarObjectId(recintoId)) {
            throw {
                success: false,
                error: 'ID de recinto inválido',
                code: 'INVALID_RECINTO_ID'
            };
        }

        const response = await instance.get(`/matriculas/estadisticas/${recintoId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// ========================================
// CONSULTAS DE USUARIO
// ========================================

/**
 * Obtener mis matrículas (usuario autenticado)
 * @returns {Promise<object>} Lista de matrículas del usuario
 */
const ObtenerMisMatriculas = async () => {
    try {
        const response = await instance.get("/matriculas/mis-matriculas");
        return response.data; // 🆕 Consistencia
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * ¿El inscrito tiene matrícula en el recinto? Si no, cuánto le costaría.
 * @returns {Promise<object>} { requiereMatricula, tieneMatricula, venceEl, precio, tipoPrecio, textoVigencia, ... }
 */
const EstadoInscrito = async ({ rut, recintoId, fechaNacimiento }) => {
    try {
        const response = await instance.post("/matriculas/estado-inscrito", { rut, recintoId, fechaNacimiento });
        return response?.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

/**
 * Carga masiva de matriculados. `aplicar: false` = vista previa (no graba).
 */
const CargaMasiva = async (recintoId, filas, aplicar = false) => {
    try {
        const response = await instance.post(`/matriculas/recinto/${recintoId}/carga-masiva`, { filas, aplicar });
        return response?.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Nómina de funcionarios (precio funcionario)
const ListarFuncionarios = async (params = {}) => {
    try {
        const response = await instance.get("/matriculas/funcionarios", { params });
        return response?.data || [];
    } catch (error) {
        throw error.response?.data || error;
    }
};

const AgregarFuncionario = async (data) => {
    try {
        const response = await instance.post("/matriculas/funcionarios", data);
        return response?.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

const QuitarFuncionario = async (id) => {
    try {
        const response = await instance.delete(`/matriculas/funcionarios/${id}`);
        return response?.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

const CargaMasivaFuncionarios = async (filas, aplicar = false) => {
    try {
        const response = await instance.post("/matriculas/funcionarios/carga-masiva", { filas, aplicar });
        return response?.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// ========================================
// EXPORTS
// ========================================

export default {
    EstadoInscrito,
    CargaMasiva,
    ListarFuncionarios,
    AgregarFuncionario,
    QuitarFuncionario,
    CargaMasivaFuncionarios,

    // Configuración (Admin)
    ConfigurarMatricula,
    ObtenerMatriculaRecinto,
    VerificarConfiguracionRecinto,
    ListarMatriculas,
    DesactivarMatricula,
    ObtenerRecintosConMatricula,

    // Validación y Verificación
    VerificarMatriculaUsuario,
    ValidarParaInscripcion,
    
    // Proceso de Compra (Usuario)
    CrearPagoMatricula,
    ConfirmarPagoMatricula,
    
    // Gestión de Usuarios Matriculados (Admin)
    ListarUsuariosMatriculados,
    AgregarMatriculaManual,
    ObtenerHistorialUsuario,
    DesactivarMatriculaUsuario,
    ObtenerEstadisticas,
    
    // Consultas de Usuario
    ObtenerMisMatriculas,
};
